/**
 * AuditLivraisonsModal - Audit des livraisons suspectes
 * Détecte les VENTE marquées LIVRE/VALIDE sans source ou sans attribution
 */
import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { ShieldAlert, Download, Undo2, Loader2, X, Search, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = '';

export const AuditLivraisonsModal = ({ isOpen, onClose, onSuccess, project = "WORLD CUP 2026" }) => {
  const [loading, setLoading] = useState(false);
  const [undelivering, setUndelivering] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [audited, setAudited] = useState(false);

  const handleAudit = useCallback(async () => {
    setLoading(true);
    setTickets([]);
    setSelected(new Set());
    setAudited(false);
    try {
      const res = await fetch(`${API_URL}/api/tickets/audit-livraisons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project })
      });
      const data = await res.json();
      if (data.success) {
        setTickets(data.tickets || []);
        setAudited(true);
        if (data.count === 0) {
          toast.success('Aucune anomalie détectée !');
        } else {
          toast.warning(`${data.count} ticket(s) suspect(s) trouvé(s)`);
        }
      } else {
        toast.error(data.message || 'Erreur audit');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  }, [project]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === tickets.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(tickets.map(t => t.id)));
    }
  };

  const handleUndeliver = async () => {
    if (selected.size === 0) {
      toast.error('Sélectionnez au moins un ticket');
      return;
    }
    setUndelivering(true);
    try {
      const res = await fetch(`${API_URL}/api/tickets/undeliver`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket_ids: Array.from(selected), project })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        // Retirer les tickets dé-livrés de la liste
        setTickets(prev => prev.filter(t => !selected.has(t.id)));
        setSelected(new Set());
        if (onSuccess) onSuccess();
      } else {
        toast.error(data.message || 'Erreur');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    } finally {
      setUndelivering(false);
    }
  };

  const handleExportCSV = () => {
    if (tickets.length === 0) return;
    const headers = ['Ref Vente', 'Ref Achat', 'Événement', 'Catégorie', 'Source', 'Attribué', 'Statut', 'Client', 'Email', 'Prix', 'ACHAT liés', 'Raisons'];
    const rows = tickets.map(t => [
      t.ref_vente,
      t.ref_achat,
      t.evenement,
      t.categorie,
      t.source || '',
      t.attribue ? 'Oui' : 'Non',
      t.statut,
      t.nom_client,
      t.mail_client,
      t.prix,
      t.achat_lies,
      t.raisons.join(' + ')
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_livraisons_${project.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exporté !');
  };

  const handleClose = () => {
    setTickets([]);
    setSelected(new Set());
    setAudited(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-zinc-900 border-red-500/30 text-white max-w-5xl max-h-[85vh] flex flex-col" data-testid="audit-livraisons-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-red-500" />
            Audit Livraisons Suspectes
          </DialogTitle>
          <p className="text-sm text-zinc-400 mt-1">
            Détecte les VENTE marquées LIVRE/VALIDE sans source ou sans attribution réelle
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-3">
          {/* Bouton lancer l'audit */}
          {!audited && (
            <div className="flex justify-center py-8">
              <Button
                onClick={handleAudit}
                disabled={loading}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 text-base"
                data-testid="run-audit-btn"
              >
                {loading ? (
                  <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Analyse en cours...</>
                ) : (
                  <><Search className="h-5 w-5 mr-2" /> Lancer l'audit</>
                )}
              </Button>
            </div>
          )}

          {/* Résultats */}
          {audited && tickets.length === 0 && (
            <div className="text-center py-12 text-green-400" data-testid="audit-clean">
              <ShieldAlert className="h-12 w-12 mx-auto mb-3 opacity-60" />
              <p className="text-lg font-medium">Aucune anomalie détectée</p>
              <p className="text-sm text-zinc-500 mt-1">Toutes les livraisons sont cohérentes</p>
            </div>
          )}

          {audited && tickets.length > 0 && (
            <>
              {/* Actions bar */}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-400" />
                  <span className="text-sm text-red-400 font-medium">{tickets.length} anomalie(s) détectée(s)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportCSV}
                    className="border-zinc-600 text-zinc-300"
                    data-testid="export-audit-csv"
                  >
                    <Download className="h-4 w-4 mr-1" /> Exporter CSV
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleUndeliver}
                    disabled={selected.size === 0 || undelivering}
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                    data-testid="undeliver-btn"
                  >
                    {undelivering ? (
                      <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Dé-livraison...</>
                    ) : (
                      <><Undo2 className="h-4 w-4 mr-1" /> Dé-livrer {selected.size > 0 ? `(${selected.size})` : ''}</>
                    )}
                  </Button>
                </div>
              </div>

              {/* Tableau */}
              <div className="overflow-auto flex-1 border border-zinc-700 rounded-lg" data-testid="audit-table">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-800 sticky top-0">
                    <tr>
                      <th className="px-2 py-2 text-left">
                        <Checkbox
                          checked={selected.size === tickets.length && tickets.length > 0}
                          onCheckedChange={toggleAll}
                          data-testid="select-all-audit"
                        />
                      </th>
                      <th className="px-2 py-2 text-left text-zinc-400 font-medium">Ref Vente</th>
                      <th className="px-2 py-2 text-left text-zinc-400 font-medium">Événement</th>
                      <th className="px-2 py-2 text-left text-zinc-400 font-medium">Catégorie</th>
                      <th className="px-2 py-2 text-left text-zinc-400 font-medium">Source</th>
                      <th className="px-2 py-2 text-left text-zinc-400 font-medium">Attribué</th>
                      <th className="px-2 py-2 text-left text-zinc-400 font-medium">Statut</th>
                      <th className="px-2 py-2 text-left text-zinc-400 font-medium">ACHAT liés</th>
                      <th className="px-2 py-2 text-left text-zinc-400 font-medium">Raisons</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map((t) => (
                      <tr
                        key={t.id}
                        className={`border-t border-zinc-800 hover:bg-zinc-800/50 ${selected.has(t.id) ? 'bg-red-900/20' : ''}`}
                        data-testid={`audit-row-${t.id}`}
                      >
                        <td className="px-2 py-1.5">
                          <Checkbox
                            checked={selected.has(t.id)}
                            onCheckedChange={() => toggleSelect(t.id)}
                          />
                        </td>
                        <td className="px-2 py-1.5 font-mono text-xs text-white">{t.ref_vente || '-'}</td>
                        <td className="px-2 py-1.5 text-zinc-300 truncate max-w-[180px]">{t.evenement || '-'}</td>
                        <td className="px-2 py-1.5 text-zinc-300">{t.categorie || '-'}</td>
                        <td className="px-2 py-1.5">
                          {t.source ? (
                            <span className="text-zinc-300">{t.source}</span>
                          ) : (
                            <span className="text-red-400 font-medium">VIDE</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          {t.attribue ? (
                            <span className="text-green-400">Oui</span>
                          ) : (
                            <span className="text-red-400 font-medium">Non</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-purple-500/20 text-purple-300">
                            {t.statut}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {t.achat_lies === 0 ? (
                            <span className="text-red-400 font-medium">0</span>
                          ) : (
                            <span className="text-green-400">{t.achat_lies}</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          <div className="flex flex-wrap gap-1">
                            {t.raisons.map((r, i) => (
                              <span key={i} className="px-1.5 py-0.5 rounded text-xs bg-red-500/20 text-red-300">
                                {r}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex gap-2 pt-2">
          {audited && tickets.length > 0 && (
            <Button
              variant="outline"
              onClick={handleAudit}
              disabled={loading}
              className="border-zinc-700"
              data-testid="rerun-audit-btn"
            >
              <Search className="h-4 w-4 mr-2" />
              Relancer l'audit
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleClose}
            className="border-zinc-700"
          >
            <X className="h-4 w-4 mr-2" />
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AuditLivraisonsModal;
